import React from 'react';
import { Skeleton } from './Skeleton';
import { cn } from '../../../utils/cn';
import type { SkeletonAnimation } from './Skeleton.types';

interface SkeletonStatsProps {
  /**
   * Number of stat cards to display
   * @default 4
   */
  count?: number;

  /**
   * Additional CSS classes for the grid container
   */
  className?: string;

  /**
   * Animation type
   * @default 'pulse'
   */
  animation?: SkeletonAnimation;
}

/**
 * Skeleton for a single stats card
 */
const SkeletonStatCard: React.FC<{ animation: SkeletonAnimation }> = ({ animation }) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {/* Title */}
          <Skeleton className="h-4 w-24 mb-2" animation={animation} />
          {/* Value */}
          <Skeleton className="h-8 w-20 mb-2" animation={animation} />
          {/* Description */}
          <Skeleton className="h-3 w-16" animation={animation} />
        </div>
        {/* Icon placeholder */}
        <Skeleton
          variant="rectangular"
          width={48}
          height={48}
          className="rounded-lg"
          animation={animation}
        />
      </div>
    </div>
  );
};

/**
 * Skeleton placeholder for stats card grid
 *
 * Displays a grid of stat card skeletons that match the layout
 * of dashboard statistics sections.
 */
export const SkeletonStats: React.FC<SkeletonStatsProps> = ({
  count = 4,
  className,
  animation = 'pulse',
}) => {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonStatCard key={index} animation={animation} />
      ))}
    </div>
  );
};

SkeletonStats.displayName = 'SkeletonStats';
